import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type CloneOrganizationFromQuery,
  type OrganizationsByIdQuery,
  type RepositoryFragment,
  useCloneOrganizationFromLazyQuery,
  useOrganizationsByIdLazyQuery
} from '../__generated__/apollo-hooks';

import {
  PAGE_SIZE_DEFAULT,
  REPOSITORY_CONFIRMATION_THRESHOLD
} from '../constants/general';
import { logError } from '../helpers/logger.helper';
import { fetchAllPages } from '../helpers/pagination.helper';
import { mapRepositoryFragmentToId } from '../helpers/repository.helpers';
import { useModConfirm } from './use-mod-confirm.hooks';

const getNodeWithId = ({ node }) => ({
  ...node,
  id: mapRepositoryFragmentToId(node)
});

const extractRepoPage = (result: { data?: OrganizationsByIdQuery }) => {
  const repos = result?.data?.organization?.repositories;
  return {
    hasNextPage: repos?.pageInfo?.hasNextPage ?? false,
    afterCursor: repos?.pageInfo?.endCursor,
    nodes: repos?.edges?.map(getNodeWithId) ?? []
  };
};

type CloneOrgNode = NonNullable<
  NonNullable<
    NonNullable<CloneOrganizationFromQuery['organizations']>['edges'][number]
  >['node']
>;

const extractOrgsPage = (result: { data?: CloneOrganizationFromQuery }) => {
  const orgs = result?.data?.organizations;
  return {
    hasNextPage: orgs?.pageInfo?.hasNextPage ?? false,
    afterCursor: orgs?.pageInfo?.endCursor,
    nodes: orgs?.edges?.map(({ node }) => node) ?? []
  };
};

export const useCloneOrganizations = (
  search: string
): { orgs: CloneOrgNode[]; loading: boolean } => {
  const [orgs, setOrgs] = useState<CloneOrgNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchOrgs, { fetchMore }] = useCloneOrganizationFromLazyQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchOrgs and fetchMore are stable Apollo references
  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const where = search ? { name: { _icontains: search } } : undefined;
        const nodes = await fetchAllPages({
          initialFetch: () =>
            fetchOrgs({ variables: { first: PAGE_SIZE_DEFAULT, where } }),
          fetchMore: (after) =>
            fetchMore({
              variables: { first: PAGE_SIZE_DEFAULT, after, where }
            }),
          extractPage: extractOrgsPage,
          isCancelled: () => cancelled
        });
        if (!cancelled) {
          setOrgs(nodes);
        }
      } catch (error) {
        logError(error);
        // Don't leave the previous search's results under the new query.
        if (!cancelled) {
          setOrgs([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [search]);

  return { orgs, loading };
};

// Used by flows (export, merge-selected) that already gate the call behind
// an explicit user action and handle the rejection themselves.
export const useGetAllOrganizationRepositories = (): {
  getOrganizationRepositories: (params: {
    id: string;
  }) => Promise<RepositoryFragment[]>;
} => {
  const [fetchOrganizationRepository, { fetchMore }] =
    useOrganizationsByIdLazyQuery();

  const getOrganizationRepositories = useCallback(
    ({ id }: { id: string }) =>
      fetchAllPages({
        initialFetch: () =>
          fetchOrganizationRepository({
            variables: { id, first: PAGE_SIZE_DEFAULT }
          }),
        fetchMore: (after) =>
          fetchMore({ variables: { first: PAGE_SIZE_DEFAULT, after } }),
        extractPage: extractRepoPage
      }),
    [fetchOrganizationRepository, fetchMore]
  );

  return { getOrganizationRepositories };
};

type CloneOrganizationRepositoriesProgress = {
  loaded: number;
  total: number;
};

// Fetches every page of a "copy from existing organization" source org.
// Mirrors useSelectAllRepositories: confirms above
// REPOSITORY_CONFIRMATION_THRESHOLD, reports progress, and is cancellable.
export const useCloneOrganizationRepositories = (
  id: string | undefined
): {
  repositories: RepositoryFragment[];
  loading: boolean;
  progress: CloneOrganizationRepositoriesProgress | null;
  error: boolean;
  dismissError: () => void;
  cancel: () => void;
} => {
  const [fetchOrganizationRepository, { fetchMore }] =
    useOrganizationsByIdLazyQuery();
  const [repositories, setRepositories] = useState<RepositoryFragment[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] =
    useState<CloneOrganizationRepositoriesProgress | null>(null);
  const [error, setError] = useState(false);
  const runIdRef = useRef(0);
  const confirm = useModConfirm();

  // Read via refs, not as effect deps — none of these identities are
  // guaranteed stable, and depending on them would restart the fetch loop
  // mid-run on an unrelated identity change.
  const fetchOrganizationRepositoryRef = useRef(fetchOrganizationRepository);
  fetchOrganizationRepositoryRef.current = fetchOrganizationRepository;
  const fetchMoreRef = useRef(fetchMore);
  fetchMoreRef.current = fetchMore;
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    setLoading(false);
    setProgress(null);
  }, []);

  const dismissError = useCallback(() => {
    setError(false);
  }, []);

  // Tells "the user cancelled me" (same id, safe to keep partial results)
  // apart from "a newer run superseded me" (different id, stay silent).
  const idRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    idRef.current = id;
    setError(false);
    setRepositories([]);

    if (!id) {
      setLoading(false);
      setProgress(null);
      return;
    }

    const runId = ++runIdRef.current;
    setLoading(true);

    const run = async () => {
      let result: { data: OrganizationsByIdQuery };
      try {
        result = await fetchOrganizationRepositoryRef.current({
          variables: { id, first: PAGE_SIZE_DEFAULT }
        });
      } catch (fetchError) {
        logError(fetchError as Error);
        if (runIdRef.current === runId) {
          setError(true);
          setLoading(false);
        }
        return;
      }

      if (runIdRef.current !== runId) {
        return;
      }

      const total = result.data?.organization?.repositories?.count ?? 0;

      if (total > REPOSITORY_CONFIRMATION_THRESHOLD) {
        const { confirmed } = await confirmRef.current({
          title: 'Copy all repositories?',
          description: `This will copy all ${total.toLocaleString()} repositories from this organization. This may take a while.`,
          confirmationText: 'Copy all',
          cancellationText: 'Cancel'
        });
        if (runIdRef.current !== runId) {
          return;
        }
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      // repositories is only written once the loop settles — writing it per
      // page would trigger a full grid selection-model rebuild per page.
      let { hasNextPage, afterCursor, nodes } = extractRepoPage(result);
      let loaded = nodes.length;
      setProgress({ loaded, total });

      while (hasNextPage && runIdRef.current === runId) {
        let pageResult: { data: OrganizationsByIdQuery };
        try {
          pageResult = await fetchMoreRef.current({
            variables: { first: PAGE_SIZE_DEFAULT, after: afterCursor }
          });
        } catch (fetchError) {
          logError(fetchError as Error);
          if (runIdRef.current === runId) {
            setError(true);
            setRepositories(nodes);
          }
          break;
        }

        if (runIdRef.current !== runId) {
          if (idRef.current === id) {
            setRepositories(nodes);
          }
          break;
        }

        const page = extractRepoPage(pageResult);
        hasNextPage = page.hasNextPage;
        afterCursor = page.afterCursor;
        nodes = [...nodes, ...page.nodes];
        loaded += page.nodes.length;
        setProgress({ loaded, total });
      }

      if (runIdRef.current === runId) {
        setRepositories(nodes);
        setProgress(null);
        setLoading(false);
      }
    };

    run();

    return () => {
      runIdRef.current += 1;
    };
  }, [id]);

  return { repositories, loading, progress, error, dismissError, cancel };
};

export { useSelectedOrganization } from '../contexts/organization.context';
